/**
 * TestsListingPage — the lab admin's cross-project tests hub.
 *
 * Two views:
 *   - Tests: KPI strip + filters (status chips, test-type multiselect,
 *     search) + admin table (status colors, per-test report-archive rollup,
 *     bulk sheet/report actions, preview & archive shortcuts per row).
 *   - Report Archive: every archived render + issued copy across the lab,
 *     newest first, server-paginated with search.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { FactCheck, Inventory2 } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { getAllTests } from '../../services/testListingService';
import type { TestListingRow } from '../../services/testListingService';
import { fetchReportArchiveRollup, fetchRecentReportArchive } from '../../services/reportService';
import type { ReportArchiveRollup } from '../../types/reportArtifact';
import { ReportPreviewDialog } from '../../components/reportPreview/ReportPreviewDialog';
import { ReportHistoryDialog } from '../../components/reportPreview/ReportHistoryDialog';
import { isValidationRow } from './testsPageUtils';
import { TestsKpiRow } from './TestsKpiRow';
import { TestsFilterBar } from './TestsFilterBar';
import { TestsTable } from './TestsTable';
import { TestsBulkBar } from './TestsBulkBar';
import { ReportArchiveTab } from './ReportArchiveTab';

type OpenTestTarget = Pick<TestListingRow, 'ProjectId' | 'WorkOrderKey' | 'WorkOrderDetailKey'>;

export const TestsListingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');

  const [activeTab, setActiveTab] = useState(searchParams.get('view') === 'archive' ? 1 : 0);
  const [data, setData] = useState<TestListingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    urlStatus ? urlStatus.split(',') : [],
  );
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [workOrderFilter, setWorkOrderFilter] = useState<string | null>(null);
  const [quoteFilter, setQuoteFilter] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('sysLastModifiedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rollups, setRollups] = useState<Map<string, ReportArchiveRollup>>(new Map());
  const [archiveTotal, setArchiveTotal] = useState<number | null>(null);
  const [issuedTotal, setIssuedTotal] = useState<number | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    try {
      const status = selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined;
      const rows = await getAllTests({ status, search: search || undefined });
      setData(rows);
    } catch (err) { console.error('Failed to load tests', err); }
    setLoading(false);
  }, [selectedStatuses, search]);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  // Report-archive rollup for the loaded rows (chunked service call).
  useEffect(() => {
    if (data.length === 0) { setRollups(new Map()); return; }
    let cancelled = false;
    fetchReportArchiveRollup(data.map((r) => r.WorkOrderDetailKey))
      .then((rows) => {
        if (cancelled) return;
        setRollups(new Map(rows.map((r) => [r.workOrderDetailKey, r])));
      })
      .catch(() => { /* archive column degrades to "—" */ });
    return () => { cancelled = true; };
  }, [data]);

  // Lab-wide archive totals for the KPI cards (two count-only calls).
  useEffect(() => {
    let cancelled = false;
    fetchRecentReportArchive({ limit: 1, offset: 0 })
      .then((r) => { if (!cancelled) setArchiveTotal(r.total); })
      .catch(() => { /* KPI shows 0 */ });
    fetchRecentReportArchive({ limit: 1, offset: 0, kind: 'issued' })
      .then((r) => { if (!cancelled) setIssuedTotal(r.total); })
      .catch(() => { /* KPI shows 0 */ });
    return () => { cancelled = true; };
  }, [data.length]);

  const toggleStatus = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status],
    );
  };

  const handleSearchChange = (val: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearch(val), 300);
  };

  // E2E integrity sandbox rows are plumbing, not lab work — hidden by default.
  const base = useMemo(
    () => (showValidation ? data : data.filter((r) => !isValidationRow(r))),
    [data, showValidation],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    base.forEach((r) => { counts[r.TestStatus] = (counts[r.TestStatus] || 0) + 1; });
    return counts;
  }, [base]);

  // Cascading ("nested") filter options (BUG-2026-0184): each dropdown lists
  // only the values still reachable given the OTHER active filters, so picking a
  // Project narrows the Work Order / Quote / test-type choices to that project.
  // Each facet's own current selection is always kept in its list so the choice
  // stays visible and clearable even when the other filters would hide it.
  const matchType = useCallback(
    (r: TestListingRow) => selectedTypes.length === 0 || selectedTypes.includes(r.TestName),
    [selectedTypes],
  );
  const matchProject = useCallback(
    (r: TestListingRow) => !projectFilter || r.ProjectName === projectFilter,
    [projectFilter],
  );
  const matchWorkOrder = useCallback(
    (r: TestListingRow) => !workOrderFilter || r.WorkOrderId === workOrderFilter,
    [workOrderFilter],
  );
  const matchQuote = useCallback(
    (r: TestListingRow) => !quoteFilter || r.QuoteNumber === quoteFilter,
    [quoteFilter],
  );

  const testTypes = useMemo(
    () => [...new Set([
      ...base.filter((r) => matchProject(r) && matchWorkOrder(r) && matchQuote(r))
        .map((r) => r.TestName).filter(Boolean),
      ...selectedTypes,
    ])].sort(),
    [base, matchProject, matchWorkOrder, matchQuote, selectedTypes],
  );
  const projects = useMemo(
    () => [...new Set([
      ...base.filter((r) => matchType(r) && matchWorkOrder(r) && matchQuote(r))
        .map((r) => r.ProjectName).filter(Boolean),
      ...(projectFilter ? [projectFilter] : []),
    ])].sort(),
    [base, matchType, matchWorkOrder, matchQuote, projectFilter],
  );
  const workOrders = useMemo(
    () => [...new Set([
      ...base.filter((r) => matchType(r) && matchProject(r) && matchQuote(r))
        .map((r) => r.WorkOrderId).filter(Boolean),
      ...(workOrderFilter ? [workOrderFilter] : []),
    ])].sort(),
    [base, matchType, matchProject, matchQuote, workOrderFilter],
  );
  const quotes = useMemo(
    () => [...new Set([
      ...base.filter((r) => matchType(r) && matchProject(r) && matchWorkOrder(r))
        .map((r) => r.QuoteNumber).filter((q): q is string => !!q),
      ...(quoteFilter ? [quoteFilter] : []),
    ])].sort(),
    [base, matchType, matchProject, matchWorkOrder, quoteFilter],
  );

  const filtered = useMemo(
    () => base.filter((r) => matchType(r) && matchProject(r) && matchWorkOrder(r) && matchQuote(r)),
    [base, matchType, matchProject, matchWorkOrder, matchQuote],
  );

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortBy] ?? '';
      const bv = (b as unknown as Record<string, unknown>)[sortBy] ?? '';
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDir]);

  const handleSort = (field: string) => {
    if (field === sortBy) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(field); setSortDir('asc'); }
  };

  const openTest = useCallback((row: OpenTestTarget) => {
    if (row.ProjectId && row.WorkOrderKey && row.WorkOrderDetailKey) {
      navigate(ROUTES.TEST_PARAMETERS.replace(':projectId', row.ProjectId)
        .replace(':workOrderId', row.WorkOrderKey)
        .replace(':workOrderDetailKey', row.WorkOrderDetailKey));
    }
  }, [navigate]);

  const toggleRow = (wodKey: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(wodKey)) next.delete(wodKey);
      else next.add(wodKey);
      return next;
    });
  };

  const toggleAll = (pageKeys: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = pageKeys.every((k) => next.has(k));
      pageKeys.forEach((k) => { if (allSelected) next.delete(k); else next.add(k); });
      return next;
    });
  };

  const hasAnyFilter = selectedStatuses.length > 0 || selectedTypes.length > 0 || search.length > 0
    || !!projectFilter || !!workOrderFilter || !!quoteFilter;
  const title = activeTab === 1 ? 'Report Archive'
    : selectedStatuses.length === 0 ? 'All Tests'
      : selectedStatuses.length === 1 ? `${selectedStatuses[0]} Tests`
        : `Tests (${selectedStatuses.length} filters)`;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {activeTab === 0
              ? `${filtered.length} of ${base.length} tests across all projects`
              : 'Every archived render and issued copy, lab-wide'}
          </Typography>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab icon={<FactCheck sx={{ fontSize: 18 }} />} iconPosition="start" label="Tests" />
          <Tab icon={<Inventory2 sx={{ fontSize: 18 }} />} iconPosition="start" label="Report Archive" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <TestsKpiRow
            rows={filtered}
            archiveTotal={archiveTotal}
            issuedTotal={issuedTotal}
            onStatusClick={setSelectedStatuses}
            onArchiveClick={() => setActiveTab(1)}
          />
          <TestsFilterBar
            onSearchChange={handleSearchChange}
            selectedStatuses={selectedStatuses}
            onToggleStatus={toggleStatus}
            onClearStatuses={() => setSelectedStatuses([])}
            statusCounts={statusCounts}
            totalCount={base.length}
            testTypes={testTypes}
            selectedTypes={selectedTypes}
            onTypesChange={setSelectedTypes}
            projects={projects}
            projectFilter={projectFilter}
            onProjectChange={setProjectFilter}
            workOrders={workOrders}
            workOrderFilter={workOrderFilter}
            onWorkOrderChange={setWorkOrderFilter}
            quotes={quotes}
            quoteFilter={quoteFilter}
            onQuoteChange={setQuoteFilter}
            showValidation={showValidation}
            onToggleValidation={setShowValidation}
            onClearAll={() => {
              setSelectedStatuses([]); setSelectedTypes([]); setSearch('');
              setProjectFilter(null); setWorkOrderFilter(null); setQuoteFilter(null);
            }}
            hasAnyFilter={hasAnyFilter}
          />
          <TestsBulkBar
            selectedKeys={[...selected]}
            rowInfoByKey={Object.fromEntries(
              data
                .filter((r) => selected.has(r.WorkOrderDetailKey))
                .map((r) => [r.WorkOrderDetailKey, {
                  testName: r.TestName, workOrderId: r.WorkOrderId, projectId: r.ProjectId,
                }]),
            )}
            onClear={() => setSelected(new Set())}
          />
          <TestsTable
            rows={sorted}
            rollups={rollups}
            loading={loading}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            selected={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
            onOpenTest={openTest}
            onPreview={(row) => setPreviewKey(row.WorkOrderDetailKey)}
            onHistory={(row) => setHistoryKey(row.WorkOrderDetailKey)}
          />
        </>
      )}

      {activeTab === 1 && <ReportArchiveTab onOpenTest={openTest} />}

      {previewKey && (
        <ReportPreviewDialog open onClose={() => setPreviewKey(null)} workOrderDetailKey={previewKey} />
      )}
      {historyKey && (
        <ReportHistoryDialog open onClose={() => setHistoryKey(null)} workOrderDetailKey={historyKey} />
      )}
    </Box>
  );
};
